import formatDate from "../../_src_shared/utils/functions/formatDate";
import formatDateTime from "../../_src_shared/utils/functions/formatDateTime";

/**
 * Value formatting for the submission PDF, per §4.1 of the design spec.
 *
 * The rule the whole document hangs on: a value is either machine-ish (money,
 * dates, phone, IDs, percentages, file sizes → monospaced) or human (names,
 * addresses, statuses, product names → sans). Each formatter below returns the
 * string; the caller passes `mono` alongside it.
 *
 * Absence is information in lending, so an empty value renders as an em dash and
 * the row is never dropped.
 */
export const EMPTY = "—";

export const isBlank = (v) => v === null || v === undefined || v === "";

export const money = (v, { cents = true } = {}) => {
  if (isBlank(v)) return EMPTY;
  const n = Number(v);
  if (!Number.isFinite(n)) return EMPTY;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
};

/**
 * Calendar dates are UTC-pinned so the day never shifts.
 *
 * `formatDate` hands the value to dayjs, which reads a full ISO instant in the
 * LOCAL zone — so a date-only field stored as `2026-09-11T00:00:00.000Z` printed
 * as Sep 10 anywhere west of UTC. Trimming to the date part makes dayjs parse it
 * as local midnight on the day that was actually stored. Date of birth and
 * expected closing both go through here, so the shift was not cosmetic.
 */
const calendarDay = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : v;

// "Aug 7, 2026".
export const dateOnly = (v) =>
  isBlank(v) ? EMPTY : formatDate(calendarDay(v), EMPTY, "MMM D, YYYY");

// Short form for the metrics band. "Aug 7".
export const dateShort = (v) =>
  isBlank(v) ? EMPTY : formatDate(calendarDay(v), EMPTY, "MMM D");

// Instants: Eastern, per the app-wide rule. "Jul 23, 2026, 2:28 PM".
export const dateTime = (v) =>
  isBlank(v) ? EMPTY : formatDateTime(v, "MMM D, YYYY, h:mm A", EMPTY);

// The day an instant fell on, in the same zone `dateTime` renders it in — for
// the signature line, which dates the attestation without timing it.
export const dateTimeDay = (v) =>
  isBlank(v) ? EMPTY : formatDateTime(v, "MMM D, YYYY", EMPTY);

export const percent = (fraction, dp = 1) =>
  fraction === null || fraction === undefined || !Number.isFinite(fraction)
    ? EMPTY
    : `${(fraction * 100).toFixed(dp)}%`;

// "13863663645" → "(386) 366-3645". Leaves anything that isn't 10/11 digits
// alone rather than mangling an international or partial number.
export const phone = (v) => {
  if (isBlank(v)) return EMPTY;
  const digits = String(v).replace(/\D/g, "");
  const local =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return String(v);
  return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
};

// Always masked on paper — a PDF gets mailed and filed.
export const ssn = (v) =>
  isBlank(v) ? EMPTY : `•••-••-${String(v).slice(-4)}`;

// "Greater than 720" → "> 720"; "Below 600" → "< 600"; bands pass through.
export const ficoBand = (v) => {
  if (isBlank(v)) return EMPTY;
  const s = String(v).trim();
  const gt = s.match(/^greater than\s+(\d+)$/i);
  if (gt) return `> ${gt[1]}`;
  const lt = s.match(/^(?:below|less than)\s+(\d+)$/i);
  if (lt) return `< ${lt[1]}`;
  return s;
};

// Expand stored codes; leave product names and anything unrecognised verbatim.
const PROPERTY_TYPES = {
  SFR: "Single-family residence",
  PUD: "Planned unit development",
  MFR: "Multifamily residence",
  CONDO: "Condominium",
  TOWNHOME: "Townhome",
};

export const propertyType = (v) => {
  if (isBlank(v)) return EMPTY;
  return PROPERTY_TYPES[String(v).trim().toUpperCase()] || String(v);
};

export const fileSize = (bytes) => {
  if (isBlank(bytes)) return EMPTY;
  const mb = bytes / 1048576;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

export const text = (v) => (isBlank(v) ? EMPTY : String(v));

// Days from today to a date, or null. Drives the amber "closing is imminent"
// rule in the metrics band.
export const daysUntil = (v) => {
  if (isBlank(v)) return null;
  const then = new Date(v).getTime();
  if (Number.isNaN(then)) return null;
  return Math.ceil((then - Date.now()) / 86400000);
};
